/**
 * Popup am Kreis-Pfeile-Knopf (AppLayout): listet alle offenen
 * Sync-Probleme auf (siehe offline-sync.service.ts SyncIssue) und lässt sie
 * gezielt auflösen, statt sie stumm zu verschlucken. Öffnet sich erneut bei
 * jedem Klick auf den Knopf, solange noch etwas offen ist.
 */
import { h, mount } from "@/lib/dom";
import { button, openModal, emptyState, toast } from "@/components/ui";
import { navigate } from "@/app/router";
import { offlineSync, type SyncIssue } from "@/services/offline/offline-sync.service";

export async function openSyncIssuesDialog(): Promise<void> {
  const list = h("div", { class: "col" });
  const handle = openModal({
    title: "Synchronisierung",
    guardUnsaved: false,
    body: list,
    footer: h("div", { class: "row" }, button({ label: "Schließen", variant: "ghost", onClick: () => handle.close() })),
  });

  async function refresh(): Promise<void> {
    const issues = await offlineSync.loadIssues();
    mount(
      list,
      issues.length
        ? h("div", { class: "col" }, ...issues.map((issue) => issueRow(issue, refresh)))
        : h(
            "div",
            { class: "card-pad" },
            emptyState({ icon: "check", title: "Alles erledigt", text: "Keine offenen Probleme mehr." })
          )
    );
  }
  await refresh();
}

function issueRow(issue: SyncIssue, onChanged: () => void): HTMLElement {
  const run = (fn: () => Promise<void>) => () =>
    void fn()
      .then(onChanged)
      .catch((err) => toast(err instanceof Error ? err.message : "Fehlgeschlagen.", "error"));

  const actions: HTMLElement[] = [];
  if (issue.kind === "pdf-conflict" && issue.topicId) {
    actions.push(
      button({ label: "Thema öffnen", size: "sm", onClick: () => navigate(`/topic/${issue.topicId}/document`) }),
      button({
        label: "Offline-Version verwerfen",
        size: "sm",
        variant: "ghost",
        onClick: run(() => offlineSync.discardPdfConflict(issue.topicId!)),
      })
    );
  } else if (issue.kind === "note-merged" && issue.topicId) {
    actions.push(
      button({ label: "Thema öffnen", size: "sm", onClick: () => navigate(`/topic/${issue.topicId}`) }),
      button({ label: "Verstanden", size: "sm", variant: "ghost", onClick: run(() => offlineSync.dismissIssue(issue.id)) })
    );
  } else if (issue.kind === "op-error") {
    actions.push(
      button({ label: "Erneut versuchen", size: "sm", onClick: run(() => offlineSync.retryOpIssue(issue)) }),
      button({ label: "Verwerfen", size: "sm", variant: "ghost", onClick: run(() => offlineSync.discardOpIssue(issue)) })
    );
  }

  return h(
    "div",
    { class: "card card-pad sync-issue-row" },
    h("p", { class: "text-sm" }, issue.message),
    h("div", { class: "row" }, ...actions)
  );
}
